import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Typography, Paper, TextField, RadioGroup, Radio,
  FormControlLabel, FormControl, Select, MenuItem, InputLabel,
  Chip, LinearProgress, Divider, IconButton, Tooltip,
} from '@mui/material';
import { PlayArrow, SkipNext, Stop, ContentCopy } from '@mui/icons-material';
import api from '../api/client';

interface Script {
  id: number;
  name: string;
  description: string;
  call_type_code: string;
}

interface ScriptNode {
  id: number;
  script_id: number;
  node_type: string;
  sort_order: number;
  question_text: string;
  input_type: string;
  options: { choices?: string[] } | null;
  narrative_template: string;
  is_required: boolean;
  help_text: string;
}

interface Props {
  callTypeCode?: string;
  onNarrativeUpdate?: (narrative: string) => void;
  onClose?: () => void;
}

export default function CaseEntryPanel({ callTypeCode, onNarrativeUpdate, onClose }: Props) {
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedScriptId, setSelectedScriptId] = useState<number | null>(null);
  const [started, setStarted] = useState(false);
  const [currentNode, setCurrentNode] = useState<ScriptNode | null>(null);
  const [answer, setAnswer] = useState('');
  const [narrativeLines, setNarrativeLines] = useState<string[]>([]);
  const [totalNodes, setTotalNodes] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/case-entry/scripts/active').then((r) => {
      setScripts(r.data);
      // Auto-select script matching call type
      if (callTypeCode) {
        const match = r.data.find((s: Script) => s.call_type_code.toLowerCase() === callTypeCode.toLowerCase());
        if (match) setSelectedScriptId(match.id);
      }
    });
  }, [callTypeCode]);

  const startScript = async () => {
    if (!selectedScriptId) return;
    setLoading(true);
    try {
      const r = await api.get(`/case-entry/scripts/${selectedScriptId}/start`);
      setCurrentNode(r.data.first_node);
      setTotalNodes(r.data.total_nodes);
      setStarted(true);
      setNarrativeLines([]);
      setAnsweredCount(0);
      setIsComplete(false);
    } catch {}
    setLoading(false);
  };

  const submitAnswer = async () => {
    if (!currentNode || !selectedScriptId) return;
    if (currentNode.is_required && !answer.trim()) return;

    setLoading(true);
    try {
      const r = await api.post(`/case-entry/scripts/${selectedScriptId}/execute`, {
        node_id: currentNode.id,
        answer: answer.trim() || '(skipped)',
      });

      if (r.data.narrative_line) {
        setNarrativeLines((prev) => [...prev, r.data.narrative_line]);
      }
      setAnsweredCount((c) => c + 1);

      if (r.data.is_complete) {
        setIsComplete(true);
        setCurrentNode(null);
        // Build final narrative
        const allLines = [...narrativeLines, r.data.narrative_line].filter(Boolean);
        if (onNarrativeUpdate) onNarrativeUpdate(allLines.join('\n'));
      } else {
        setCurrentNode(r.data.next_node);
        setAnswer('');
      }
    } catch {}
    setLoading(false);
  };

  const skipQuestion = async () => {
    if (!currentNode || !selectedScriptId) return;
    setLoading(true);
    try {
      const r = await api.post(`/case-entry/scripts/${selectedScriptId}/execute`, {
        node_id: currentNode.id,
        answer: '(skipped)',
      });
      setAnsweredCount((c) => c + 1);
      if (r.data.is_complete) {
        setIsComplete(true);
        setCurrentNode(null);
        const allLines = [...narrativeLines].filter(Boolean);
        if (onNarrativeUpdate) onNarrativeUpdate(allLines.join('\n'));
      } else {
        setCurrentNode(r.data.next_node);
        setAnswer('');
      }
    } catch {}
    setLoading(false);
  };

  const copyNarrative = () => {
    const text = narrativeLines.join('\n');
    navigator.clipboard.writeText(text);
  };

  const progress = totalNodes > 0 ? (answeredCount / (totalNodes - 1)) * 100 : 0; // -1 for end node

  // Not started — show script picker
  if (!started) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Case Entry</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a script to guide you through the interview questions.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Interview Script</InputLabel>
          <Select
            value={selectedScriptId || ''}
            label="Interview Script"
            onChange={(e) => setSelectedScriptId(Number(e.target.value))}
          >
            {scripts.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name} {s.call_type_code && `(${s.call_type_code})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={!selectedScriptId}
            onClick={startScript}
            size="small"
          >
            Start Interview
          </Button>
          {onClose && <Button size="small" onClick={onClose}>Cancel</Button>}
        </Box>
      </Paper>
    );
  }

  // Complete
  if (isComplete) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: 'success.main' }}>
          Interview Complete
        </Typography>
        <LinearProgress variant="determinate" value={100} color="success" sx={{ mb: 2 }} />
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default', maxHeight: 200, overflow: 'auto' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {narrativeLines.join('\n')}
          </Typography>
        </Paper>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Copy to clipboard">
            <Button size="small" startIcon={<ContentCopy />} onClick={copyNarrative}>Copy Narrative</Button>
          </Tooltip>
          <Button size="small" variant="outlined" onClick={() => { setStarted(false); setIsComplete(false); setNarrativeLines([]); }}>
            Start Over
          </Button>
          {onClose && <Button size="small" onClick={onClose}>Close</Button>}
        </Box>
      </Paper>
    );
  }

  // Active interview
  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Case Entry — Q{answeredCount + 1}
        </Typography>
        <Chip size="small" label={`${answeredCount}/${totalNodes - 1}`} />
      </Box>
      <LinearProgress variant="determinate" value={Math.min(progress, 100)} sx={{ mb: 2 }} />

      {currentNode && (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {currentNode.question_text}
          </Typography>

          {currentNode.help_text && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1 }}>
              {currentNode.help_text}
            </Typography>
          )}

          {/* Input based on type */}
          {(currentNode.input_type === 'yes_no' || currentNode.input_type === 'multiple_choice') && currentNode.options?.choices && (
            <RadioGroup value={answer} onChange={(e) => setAnswer(e.target.value)} sx={{ mb: 1 }}>
              {currentNode.options.choices.map((choice) => (
                <FormControlLabel key={choice} value={choice} control={<Radio size="small" />} label={choice} />
              ))}
            </RadioGroup>
          )}

          {currentNode.input_type === 'select' && currentNode.options?.choices && (
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <Select value={answer} onChange={(e) => setAnswer(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>Select...</MenuItem>
                {currentNode.options.choices.map((choice) => (
                  <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {currentNode.input_type === 'text' && (
            <TextField
              fullWidth size="small" sx={{ mb: 1 }}
              value={answer} onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter response..."
              onKeyDown={(e) => { if (e.key === 'Enter') submitAnswer(); }}
            />
          )}

          {currentNode.input_type === 'numeric' && (
            <TextField
              fullWidth size="small" type="number" sx={{ mb: 1 }}
              value={answer} onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter number..."
              onKeyDown={(e) => { if (e.key === 'Enter') submitAnswer(); }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              variant="contained" size="small"
              onClick={submitAnswer}
              disabled={loading || (currentNode.is_required && !answer.trim())}
            >
              Next
            </Button>
            {!currentNode.is_required && (
              <Button size="small" startIcon={<SkipNext />} onClick={skipQuestion} disabled={loading}>
                Skip
              </Button>
            )}
            <Button size="small" color="error" startIcon={<Stop />} onClick={() => {
              setIsComplete(true);
              setCurrentNode(null);
              if (onNarrativeUpdate) onNarrativeUpdate(narrativeLines.join('\n'));
            }}>
              End
            </Button>
          </Box>
        </Box>
      )}

      {/* Running narrative */}
      {narrativeLines.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Narrative so far:</Typography>
          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'background.default', maxHeight: 120, overflow: 'auto' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 11 }}>
              {narrativeLines.join('\n')}
            </Typography>
          </Paper>
        </Box>
      )}
    </Paper>
  );
}
